import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

interface ThemeState {
  /** User's chosen preference. Default: 'dark' */
  preference: ThemePreference;
  /** The actual theme applied (resolved from preference + system). Default: 'dark' */
  resolvedTheme: ResolvedTheme;
  /** Set user preference and update resolved theme + DOM class */
  setPreference: (pref: ThemePreference) => void;
  /** Initialize system media query listener. Call once on app mount. Returns cleanup function. */
  initSystemListener: () => () => void;
  /** Compute and apply resolved theme to <html> element */
  applyTheme: () => void;
}

/**
 * Resolve the effective theme based on user preference and OS setting.
 */
function resolveTheme(preference: ThemePreference): ResolvedTheme {
  if (preference === 'dark') return 'dark';
  if (preference === 'light') return 'light';
  // preference === 'system'
  try {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
  } catch (_e) {
    // Gracefully handle environments without matchMedia
  }
  return 'dark';
}

/**
 * Apply the resolved theme to the DOM by toggling the 'dark' class on <html>.
 */
function applyThemeToDOM(resolved: ResolvedTheme): void {
  try {
    if (typeof document !== 'undefined' && document.documentElement) {
      if (resolved === 'dark') {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }
  } catch (_e) {
    // Gracefully handle environments without DOM
  }
}

/**
 * Actions are defined as module-level functions that operate on the store.
 * This ensures they survive useThemeStore.setState({...}, true) calls in tests,
 * since they're re-attached via the store's subscribe + getState pattern.
 */
const setPreference = (pref: ThemePreference) => {
  const resolved = resolveTheme(pref);
  applyThemeToDOM(resolved);
  useThemeStore.setState({ preference: pref, resolvedTheme: resolved });
};

const applyTheme = () => {
  const { preference } = useThemeStore.getState();
  const resolved = resolveTheme(preference);
  applyThemeToDOM(resolved);
  useThemeStore.setState({ resolvedTheme: resolved });
};

const initSystemListener = () => {
  try {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return () => {};
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handler = (_event: MediaQueryListEvent) => {
      const { preference } = useThemeStore.getState();
      if (preference === 'system') {
        const resolved = resolveTheme('system');
        applyThemeToDOM(resolved);
        useThemeStore.setState({ resolvedTheme: resolved });
      }
    };

    // Register the event listener for media query changes
    mediaQuery.addEventListener('change', handler);

    // Also set onchange for additional compatibility and test access
    (mediaQuery as any).onchange = handler;

    return () => {
      mediaQuery.removeEventListener('change', handler);
      (mediaQuery as any).onchange = null;
    };
  } catch (_e) {
    return () => {};
  }
};

export const useThemeStore = create<ThemeState>()(
  persist(
    () => ({
      preference: 'dark' as ThemePreference,
      resolvedTheme: 'dark' as ResolvedTheme,
      setPreference,
      initSystemListener,
      applyTheme,
    }),
    {
      name: 'theme-storage',
      partialize: (state) => ({ preference: state.preference }),
      // Use a dynamic storage wrapper so tests can mock global.localStorage
      // and the persist middleware will pick up the mock.
      storage: {
        getItem: (name: string) => {
          try {
            const value = localStorage.getItem(name);
            return value ? JSON.parse(value) : null;
          } catch (_e) {
            return null;
          }
        },
        setItem: (name: string, value: unknown) => {
          try {
            localStorage.setItem(name, JSON.stringify(value));
          } catch (_e) {
            // Gracefully handle storage errors
          }
        },
        removeItem: (name: string) => {
          try {
            localStorage.removeItem(name);
          } catch (_e) {
            // Gracefully handle storage errors
          }
        },
      },
    }
  )
);

// Re-inject action references whenever state is replaced (e.g., by tests calling setState({...}, true)).
// This ensures getState() always includes the action functions.
const originalSetState = useThemeStore.setState.bind(useThemeStore);
useThemeStore.setState = (partial: any, replace?: boolean) => {
  originalSetState(partial, replace);
  // After a replace, re-attach action functions if they're missing
  const state = useThemeStore.getState();
  if (!state.setPreference || !state.initSystemListener || !state.applyTheme) {
    originalSetState({
      setPreference,
      initSystemListener,
      applyTheme,
    });
  }
};
